// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import {
  ConsumptionHistoryQuerySchema,
  ConsumptionHistoryResponseSchema,
  endpoints,
  type ConsumptionHistoryQuery,
  type ConsumptionHistoryResponse,
  type PlanResponse,
} from "@bitebalance/contracts"
import { webGet, type WebRequestDeps } from "./webGet"

/**
 * Consumption history (ADR-056, 2026-09-05): GET /v1/history/meals?from&to —
 * what the user recorded as eaten, as IMMUTABLE history keyed by (meal date,
 * slot). Plan revisions re-issue wire meal ids (`breakfast_d0` names a
 * different day after a rebuild), so the Plan page decorates its cards from
 * these records and never from a wire id. The generated contracts client
 * has no named method for the operation yet; this web-only wrapper reads
 * the path from the SAME endpoint table the client and the mock server use
 * and validates the response with the contract schema (webGet.ts).
 */
const HISTORY_ENDPOINT = endpoints.find(e => e.operationId === "getConsumptionHistory")
if (!HISTORY_ENDPOINT) {
  throw new Error("@bitebalance/contracts has no getConsumptionHistory endpoint — rebuild packages/contracts")
}
const HISTORY_PATH = HISTORY_ENDPOINT.path

/** The contract caps one query at 62 days inclusive (services/planning HISTORY_MAX_DAYS). */
export const HISTORY_MAX_SPAN_DAYS = 62

export type WithConsumptionHistory<C> = C & {
  /** Canonical consumption records per day between `from` and `to` (inclusive, ≤ 62 days). */
  getConsumptionHistory: (query: ConsumptionHistoryQuery) => Promise<ConsumptionHistoryResponse>
}

export function withConsumptionHistory<C extends object>(client: C, deps: WebRequestDeps): WithConsumptionHistory<C> {
  return {
    ...client,
    getConsumptionHistory: (query: ConsumptionHistoryQuery) => {
      const { from, to } = ConsumptionHistoryQuerySchema.parse(query)
      return webGet(deps, HISTORY_PATH, { from, to }, ConsumptionHistoryResponseSchema)
    },
  }
}

/** `YYYY-MM-DD` ± days, in the calendar (no time zone arithmetic). */
export function addDaysIso(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

function spanDaysInclusive(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1
}

/**
 * The history range the Plan page needs: every date it can show — the
 * plan's week (`weekStartDate` … +6) widened to any meal dated outside it —
 * clamped to the contract's maximum span so the query is always valid.
 */
export function historyRangeForPlan(plan: Pick<PlanResponse, "weekStartDate" | "meals">): ConsumptionHistoryQuery {
  let from = plan.weekStartDate
  let to = addDaysIso(plan.weekStartDate, 6)
  for (const meal of plan.meals) {
    if (meal.date < from) from = meal.date
    if (meal.date > to) to = meal.date
  }
  if (spanDaysInclusive(from, to) > HISTORY_MAX_SPAN_DAYS) to = addDaysIso(from, HISTORY_MAX_SPAN_DAYS - 1)
  return { from, to }
}
